import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, HostListener, Inject, Input, PLATFORM_ID, inject, signal } from '@angular/core';
import { LazyImgDirective } from '@core-mkt/services/lazy-load/lazy-load.directive';
import { StorageService } from '@core-mkt/services/storage/storage.service';
import { QueryParamDirective } from '@core-mkt/services/query-param/query-param.directive';
import { SimplifiedNav } from '../../header/header';
import type { StateList } from '../../header/header';
import { stateList } from '../../header/state-nav-data';
import { DEC_STATE_STORAGE_KEY, slugFromHref } from '../../header/simplified-nav';

@Component({
  selector: 'dec-mobile-menu',
  templateUrl: './mobile-menu.component.html',
  styleUrls: ['./mobile-menu.component.scss'],
  standalone: true,
  imports: [CommonModule, LazyImgDirective, QueryParamDirective],
})
export class MobileMenuComponent {
  /** Resolved simplified nav structure (same source as desktop, resolved once upstream). */
  @Input() simplifiedNav: SimplifiedNav;
  /** Current state display label (e.g. "California" or "Select State"). */
  @Input() stateLabel = 'Select State';
  @Input() _showLogoHref = true;

  private readonly storage = inject(StorageService);
  private readonly isBrowser: boolean;
  /** Body overflow value before the panel locked scroll, restored on close. */
  private previousBodyOverflow = '';

  /** Slide-in panel visibility. */
  panelOpen = signal(false);
  /** Currently-expanded accordion group ('driversEd' | 'more'), or null. */
  expandedGroup = signal<string | null>(null);
  /** Footer state-card expansion. */
  stateCardOpen = signal(false);

  get hasNav(): boolean {
    return !!this.simplifiedNav;
  }

  get stateSelectionList(): StateList[] {
    return stateList;
  }

  get showLogoHref(): boolean {
    return this._showLogoHref;
  }

  constructor(@Inject(PLATFORM_ID) platformId: object, @Inject(DOCUMENT) private document: Document) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  openPanel(): void {
    // Guard re-entry: opening while already open would snapshot the locked
    // 'hidden' value and leave the page permanently scroll-locked on close.
    if (this.panelOpen()) {
      return;
    }
    this.panelOpen.set(true);
    this.lockBodyScroll(true);
  }

  closePanel(): void {
    this.panelOpen.set(false);
    this.lockBodyScroll(false);
  }

  toggleGroup(key: string): void {
    this.expandedGroup.update((current) => (current === key ? null : key));
  }

  toggleStateCard(): void {
    this.stateCardOpen.update((open) => !open);
  }

  /** Persist an explicit state selection before the anchor navigates (sticks on global pages). */
  selectState(item: StateList): void {
    if (this.isBrowser) {
      this.storage.set(DEC_STATE_STORAGE_KEY, slugFromHref(item.href));
    }
  }

  /**
   * Close the panel when an in-panel link is clicked. The header persists across
   * SPA navigation, so without this the body scroll lock would survive the route
   * change. Toggle buttons (accordions, state card, close) aren't anchors, so
   * they don't trigger a close here.
   */
  onPanelClick(event: Event): void {
    const target = event.target;
    if (target instanceof Element && target.closest('a')) {
      this.closePanel();
    }
  }

  /** Close the panel on Escape — focus may still be on the hamburger trigger, so listen at the document level. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.panelOpen()) {
      this.closePanel();
    }
  }

  private lockBodyScroll(lock: boolean): void {
    if (!this.isBrowser) {
      return;
    }
    if (lock) {
      this.previousBodyOverflow = this.document.body.style.overflow;
      this.document.body.style.overflow = 'hidden';
    } else {
      // Restore whatever overflow was set before we locked (don't clobber other scroll locks).
      this.document.body.style.overflow = this.previousBodyOverflow;
    }
  }
}
